/* ─────────────────────────────────────────
   Variant D: オープニング演出（PKSHA風）
   ① SVGライン描画 → ② 文字1文字ずつ出現
   → ③ 見出し行ごとスライドアップ
   → ④ ロゴ画像フェードイン → ⑤ CTAボタン
───────────────────────────────────────── */
/** 行ごとの見出しデータ */
const H1_LINES = [
  { text: '複雑なシステムを、', colored: false },
  { text: 'シンプルに届ける。', colored: true },
  { text: 'それこそが、我々の仕事です。', colored: false }
]

/** 禁則処理: 句読点・閉じ括弧を前の文字とグループ化 */
const KINSOKU = /[、。，．！？!?）)」』】〉》～…・：；]/

function splitWithKinsoku(text: string): string[] {
  const chars = text.split('')
  const chunks: string[] = []
  for (let i = 0; i < chars.length; i++) {
    if (i > 0 && KINSOKU.test(chars[i])) {
      chunks[chunks.length - 1] += chars[i]
    } else {
      chunks.push(chars[i])
    }
  }
  return chunks
}

const H1_CHUNKS: string[][] = H1_LINES.map(({ text }) => splitWithKinsoku(text))

/**
 * 各チャンクのアニメーション delay をモジュールロード時にランダム生成。
 * 行ごとの base delay に 0〜0.7s のランダム幅を加えることで、
 * 「ある瞬間に一部の文字だけブラーから復帰中」に見える。
 */
const H1_CHAR_DELAYS: number[][] = H1_CHUNKS.map((chunks, lineIdx) => {
  const base = 1.5 + lineIdx * 0.2
  return chunks.map(() => base + Math.random() * 0.7)
})

export function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen overflow-hidden bg-white"
    >
      {/* ─── 背景グラデーション ─── */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-slate-100" />

      {/* ─── SVGライン装飾（① 最初に描画） ─── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/*
          4本の流れる曲線
          - lineDrawIn : stroke-dashoffset を操作（描画）
          - waveFloat* : transform translateY を操作（波打ち）
          別プロパティなので同一要素に並列指定可能
        */}
        <path
          pathLength="1"
          fill="none"
          stroke="#c0d4e0"
          strokeWidth="1"
          d="M-60,820 C200,700 450,640 750,600 S1100,580 1350,480 S1500,360 1560,280"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 1.1s cubic-bezier(0.4,0,0.2,1) 0.05s forwards',
              'waveFloat1 7s ease-in-out 1.2s infinite'
            ].join(', ')
          }}
        />
        <path
          pathLength="1"
          fill="none"
          stroke="#b0c8d8"
          strokeWidth="1"
          d="M-60,760 C180,650 420,590 700,550 S1050,530 1300,440 S1460,330 1560,240"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 1.1s cubic-bezier(0.4,0,0.2,1) 0.18s forwards',
              'waveFloat2 9s ease-in-out 1.4s infinite'
            ].join(', ')
          }}
        />
        <path
          pathLength="1"
          fill="none"
          stroke="#ccdae8"
          strokeWidth="0.8"
          d="M-60,880 C220,790 500,740 800,700 S1100,680 1320,600"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 0.95s cubic-bezier(0.4,0,0.2,1) 0.28s forwards',
              'waveFloat3 8s ease-in-out 1.3s infinite'
            ].join(', ')
          }}
        />
        <path
          pathLength="1"
          fill="none"
          stroke="#d8e6f0"
          strokeWidth="0.6"
          d="M-60,700 C150,600 340,550 560,520 S820,500 980,440"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: 1,
            animation: [
              'lineDrawIn 0.85s cubic-bezier(0.4,0,0.2,1) 0.12s forwards',
              'waveFloat2 6s ease-in-out 1.0s infinite'
            ].join(', ')
          }}
        />
      </svg>

      {/* ─── メインコンテンツ ─── */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-12 min-h-screen grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center pt-24 lg:pt-20 pb-12">
        {/* テキスト左 */}
        <div className="flex flex-col justify-center order-2 lg:order-1">
          {/* ② ステートメント — 大文字×細字 */}
          <p className="animate-glow-focus font-extralight tracking-wide text-brand-navy/80 mb-6 md:mb-8 text-2xl md:text-4xl lg:text-[clamp(1.875rem,3vw,3.25rem)]">
            難しいから、面白い。
          </p>

          {/* ③ 見出し — 文字ごとにアニメーション（通常インラインフローで禁則処理） */}
          <h1
            className="mb-0"
            style={{ wordBreak: 'keep-all', overflowWrap: 'anywhere' }}
          >
            {H1_LINES.map(({ colored }, lineIdx) => (
              <div key={lineIdx} className=" sm:mb-3 leading-[2.5]">
                {H1_CHUNKS[lineIdx].map((chunk, chunkIdx) => (
                  <span
                    key={chunkIdx}
                    className={`inline-block text-3xl sm:text-4xl md:text-[clamp(2.25rem,4vw,3rem)] lg:text-[clamp(2.5rem,5vw,3.75rem)] font-bold leading-relaxed ${
                      colored ? 'text-brand-blue' : 'text-brand-navy'
                    }`}
                    style={{
                      animation: `charBlurIn 0.65s ease-out ${H1_CHAR_DELAYS[lineIdx][chunkIdx].toFixed(3)}s both`
                    }}
                  >
                    {chunk}
                  </span>
                ))}
              </div>
            ))}
          </h1>

          <div
            className="w-12 h-px bg-brand-cyan/50 mt-10 mb-8"
            style={{ animation: 'fadeUp 0.5s ease-out 2.2s both' }}
          />

          <p
            className="text-brand-navy/50 text-sm md:text-base lg:text-sm xl:text-base leading-loose tracking-wide mb-12 max-w-lg"
            style={{ animation: 'fadeUp 0.6s ease-out 2.4s both' }}
          >
            スマートフォンアプリからWEBシステム、Fintech、VR/ARまで。
            AIを駆使した開発手法で、最先端のソリューションを提供します。
          </p>

          {/* ⑤ CTAボタン */}
          <div
            className="flex flex-col sm:flex-row gap-4"
            style={{ animation: 'fadeUp 0.6s ease-out 2.6s both' }}
          >
            <a href="#contact" className="btn-primary">
              お問い合わせ
            </a>
            <a
              href="#features"
              className="btn-outline border-brand-navy/30 text-brand-navy/70 hover:bg-brand-navy/5 hover:border-brand-navy/60"
            >
              当社について
            </a>
          </div>
        </div>

        {/* ④ ロゴ画像右 — テキスト後にフェードイン */}
        <div
          className="relative flex flex-col items-center justify-center order-1 lg:order-2 lg:-mt-32"
          style={{ animation: 'fadeUp 0.9s ease-out 1.9s both' }}
        >
          <p
            className="relative text-sm md:text-base font-light tracking-[0.35em] text-brand-navy/40 uppercase mb-4"
            style={{ animation: 'fadeUp 0.6s ease-out 1.6s both' }}
          >
            Innovation needs simplicity
          </p>
          <div className="absolute w-80 h-80 bg-brand-cyan/10 rounded-full blur-3xl" />
          <img
            src="/images/logo.png"
            alt="J-Frontier"
            className="relative w-full max-w-md lg:max-w-lg xl:max-w-3xl h-auto drop-shadow-2xl rounded-2xl"
          />
          <div className="relative mt-4 text-center">
            <p className="text-xs font-light tracking-[0.3em] text-brand-navy/50 uppercase">
              J-Frontier Co., Ltd.
            </p>
            <p className="animate-gradient-text text-lg font-bold tracking-[0.2em] mt-0.5">
              株式会社ジェイフロンティア
            </p>
          </div>
        </div>
      </div>

      {/* スクロールインジケーター */}
      <div
        className="absolute bottom-8 left-0 right-0 z-20 hidden sm:flex flex-col items-center gap-2"
        style={{ animation: 'fadeUp 0.6s ease-out 2.9s both' }}
      >
        <span className="text-brand-navy/30 text-xs tracking-widest">
          SCROLL
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-brand-cyan/60 to-transparent animate-pulse" />
      </div>
    </section>
  )
}
